import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import {
  ActiveVisualizationModulesState,
  VisuComponentConfig
} from '@core/models/data-visualization/data-visualization.model';
import { ICONS } from '@core/services/icons/icons.service';
import { WindowRef } from '@core/services/window-ref.service';
import {
  openPopup,
  setActiveVisualizationModules,
  setFilteringState
} from '@core/store/data-visualization-popup/data-visualization-popup.actions';
import {
  toggleCalendarVisualization,
  toggleListVisualization,
  toggleMapVisualization,
  toggleOccupationVisualization,
  toggleTimelineVisualization
} from '@core/store/data-visualization/data-visualization.actions';
import {
  selectAvailableVisualizations,
  selectOpenedVisualizationWindows
} from '@core/store/data-visualization/data-visualization.selectors';
import { selectIsEditingPlanInView } from '@core/store/plan-edit/plan-edit-session.selectors';

import { DataVisuContainerComponent } from '@shared/components/data-visu/data-visu-container/data-visu-container.component';

import { ToolboxComponent, ToolboxSection } from '../toolbox/toolbox.component';

@Component({
  selector: 'app-data-visu',
  imports: [ToolboxComponent, DataVisuContainerComponent],
  templateUrl: './data-visu.component.html',
  styles: ``
})
export class DataVisuComponent {
  private readonly store = inject(Store);
  private readonly openedVisualizationWindows = this.store.selectSignal(
    selectOpenedVisualizationWindows
  );
  private readonly windowRef = inject(WindowRef);
  activeModule = signal<ActiveVisualizationModulesState>({
    calendar: false,
    list: false,
    map: false,
    packing: false,
    timeline: false,
    warehouse: false,
    occupation: false
  });
  filteringEnabled = input<boolean>(false);
  /** Forwarded to the container: whether an entity can be edited from the visualization. */
  inPlaceEditingEnabled = input<boolean>(false);
  isInsidePopup = input<boolean>(false);

  availableVisus = this.store.selectSignal(selectAvailableVisualizations);
  /** The main window hides its visualization while the popup is open, and editing is done there. */
  private readonly editingHere = this.store.selectSignal(selectIsEditingPlanInView);

  isCalendarAvailable = computed(() => this.availableVisus().has('calendar'));
  isListAvailable = computed(() => this.availableVisus().has('list'));
  isMapAvailable = computed(() => this.availableVisus().has('map'));
  isTimelineAvailable = computed(() => this.availableVisus().has('timeline'));
  isOccupationAvailable = computed(() => this.availableVisus().has('occupation'));

  isCalendarOpen = computed(
    () => this.isCalendarAvailable() && this.openedVisualizationWindows().calendar
  );
  isListOpen = computed(
    () => this.isListAvailable() && this.openedVisualizationWindows().list
  );
  isMapOpen = computed(
    () => this.isMapAvailable() && this.openedVisualizationWindows().map
  );
  isTimelineOpen = computed(
    () => this.isTimelineAvailable() && this.openedVisualizationWindows().timeline
  );
  isOccupationOpen = computed(
    () => this.isOccupationAvailable() && this.openedVisualizationWindows().occupation
  );

  message = signal('');

  private visuComponentList: VisuComponentConfig[] = [
    { id: 'map', layout: 'vertical' },
    { id: 'timeline', layout: 'horizontal' },
    { id: 'calendar', layout: 'vertical' },
    { id: 'list', layout: 'vertical' },
    { id: 'occupation', layout: 'vertical' }
  ];
  visibleComponents = computed(() => {
    const opened = this.openedVisualizationWindows();
    return this.visuComponentList.filter(
      (comp) =>
        Object.keys(opened).includes(comp.id) &&
        this.availableVisus().has(comp.id) &&
        opened[comp.id as keyof typeof opened]
    );
  });

  private translate = inject(TranslateService);
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );
  toolboxSections = computed<ToolboxSection[]>(() => {
    this.currentLang();
    const availableVisus = this.availableVisus();
    const popupRefused = !this.isInsidePopup() && this.editingHere();

    return [
      {
        id: '1',
        column: true,
        items: [
          {
            id: '0',
            icon: this.isInsidePopup()
              ? ICONS.arrowSquareIn + ' rotate-180'
              : ICONS.arrowSquareOut,
            tooltip: this.isInsidePopup()
              ? this.translate.instant('visualization.toolbox.close_popup')
              : popupRefused
                ? this.translate.instant('features.plan.edit.held.popup_disabled')
                : this.translate.instant('visualization.toolbox.open_popup'),
            disabled: popupRefused,
            callback: () => this.togglePopup()
          },
          {
            id: '1',
            icon: ICONS.mapPinSimple,
            tooltip: this.translate.instant('visualization.toolbox.map'),
            callback: () => this.toggleMap(),
            disabled: !availableVisus.has('map'),
            active: this.isMapOpen()
          },
          {
            id: '2',
            icon: ICONS.timer,
            tooltip: this.translate.instant('visualization.toolbox.timeline'),
            callback: () => this.toggleTimeline(),
            disabled: !availableVisus.has('timeline'),
            active: this.isTimelineOpen()
          },
          {
            id: '3',
            icon: ICONS.calendarBlank,
            tooltip: this.translate.instant('visualization.toolbox.calendar'),
            callback: () => this.toggleCalendar(),
            disabled: !availableVisus.has('calendar'),
            active: this.isCalendarOpen()
          },
          {
            id: '4',
            icon: ICONS.columns,
            tooltip: this.translate.instant('visualization.toolbox.list'),
            callback: () => this.toggleList(),
            disabled: !availableVisus.has('list'),
            active: this.isListOpen()
          },
          {
            id: '5',
            icon: ICONS.chartBar,
            tooltip: this.translate.instant('visualization.toolbox.occupation'),
            callback: () => this.toggleOccupation(),
            disabled: !availableVisus.has('occupation'),
            active: this.isOccupationOpen()
          },
          {
            id: '6',
            icon: ICONS.stack,
            tooltip: this.translate.instant('visualization.toolbox.packing'),
            callback: () => {
              this.message.set('Packing functionality is not implemented yet.');
            },
            disabled: !availableVisus.has('packing'),
            active: false
          }
        ]
      }
    ];
  });

  private closeDataVisualizationPopup() {
    this.windowRef.nativeWindow.close();
  }

  private openDataVisualizationPopup() {
    this.store.dispatch(openPopup());
  }

  private togglePopup() {
    if (this.isInsidePopup()) {
      this.closeDataVisualizationPopup();
    } else {
      this.dispatchVisualizationStateToPopUp();
      this.openDataVisualizationPopup();
    }
  }

  private dispatchVisualizationStateToPopUp() {
    this.store.dispatch(
      setActiveVisualizationModules({
        map: this.activeModule().map,
        calendar: this.activeModule().calendar,
        timeline: this.activeModule().timeline,
        packing: this.activeModule().packing,
        list: this.activeModule().list,
        warehouse: false,
        occupation: this.activeModule().occupation
      })
    );
    this.store.dispatch(setFilteringState({ isFilteringEnabled: this.filteringEnabled() }));
  }

  private toggleList() {
    this.store.dispatch(toggleListVisualization({ isOpen: !this.isListOpen() }));
  }

  private toggleCalendar() {
    this.store.dispatch(toggleCalendarVisualization({ isOpen: !this.isCalendarOpen() }));
  }

  private toggleTimeline() {
    this.store.dispatch(toggleTimelineVisualization({ isOpen: !this.isTimelineOpen() }));
  }

  private toggleOccupation() {
    this.store.dispatch(
      toggleOccupationVisualization({ isOpen: !this.isOccupationOpen() })
    );
  }

  private toggleMap() {
    this.store.dispatch(toggleMapVisualization({ isOpen: !this.isMapOpen() }));
  }

  protected readonly map = map;
}
